import { useRef, useEffect } from 'react';
import { TextB, TextItalic, TextUnderline, Link as LinkIcon, ListBullets, ListNumbers, TextT } from '@phosphor-icons/react';

function ToolbarBtn({ onClick, title, testId, children }) {
  return (
    <button type="button" onMouseDown={e => e.preventDefault()} onClick={onClick}
      title={title} data-testid={testId}
      className="w-8 h-8 rounded-md flex items-center justify-center hover:bg-[#E5E7EB] text-[#374151] transition-colors">
      {children}
    </button>
  );
}

/**
 * Editor WYSIWYG minimalista baseado em `contentEditable` + `document.execCommand`.
 * Suporta: bold, italic, underline, link, listas ordenadas/não-ordenadas, headings.
 * O HTML resultante é enviado ao servidor onde é sanitizado com `bleach`.
 */
export default function RichTextEditor({ value, onChange, placeholder, testId }) {
  const editorRef = useRef(null);

  // Sincroniza o value externo com o DOM apenas na montagem
  useEffect(() => {
    if (editorRef.current && !editorRef.current.innerHTML && value) {
      editorRef.current.innerHTML = value;
    }
  }, [value]);

  const exec = (cmd, arg = null) => {
    document.execCommand(cmd, false, arg);
    editorRef.current?.focus();
    onChange?.(editorRef.current?.innerHTML || '');
  };

  const insertLink = () => {
    const url = window.prompt('URL:', 'https://');
    if (!url) return;
    exec('createLink', url);
  };

  const handleInput = () => {
    onChange?.(editorRef.current?.innerHTML || '');
  };

  return (
    <div className="border border-[#E5E7EB] rounded-md overflow-hidden focus-within:ring-2 focus-within:ring-[#0F1829]/20 focus-within:border-[#0F1829]">
      <div className="flex items-center gap-0.5 border-b border-[#E5E7EB] bg-[#F9FAFB] px-2 py-1">
        <ToolbarBtn onClick={() => exec('bold')} title="Negrito (Ctrl+B)" testId="rte-bold"><TextB size={14} weight="bold" /></ToolbarBtn>
        <ToolbarBtn onClick={() => exec('italic')} title="Itálico (Ctrl+I)" testId="rte-italic"><TextItalic size={14} /></ToolbarBtn>
        <ToolbarBtn onClick={() => exec('underline')} title="Sublinhado" testId="rte-underline"><TextUnderline size={14} /></ToolbarBtn>
        <div className="w-px h-4 bg-[#E5E7EB] mx-1" />
        <ToolbarBtn onClick={() => exec('formatBlock', 'h3')} title="Título" testId="rte-h3"><TextT size={14} weight="bold" /></ToolbarBtn>
        <ToolbarBtn onClick={() => exec('insertUnorderedList')} title="Lista" testId="rte-ul"><ListBullets size={14} /></ToolbarBtn>
        <ToolbarBtn onClick={() => exec('insertOrderedList')} title="Lista numerada" testId="rte-ol"><ListNumbers size={14} /></ToolbarBtn>
        <div className="w-px h-4 bg-[#E5E7EB] mx-1" />
        <ToolbarBtn onClick={insertLink} title="Inserir link" testId="rte-link"><LinkIcon size={14} /></ToolbarBtn>
      </div>
      <div
        ref={editorRef}
        data-testid={testId}
        contentEditable
        suppressContentEditableWarning
        onInput={handleInput}
        onBlur={handleInput}
        data-placeholder={placeholder || 'Escreva aqui…'}
        className="p-3 text-sm min-h-[180px] max-h-[400px] overflow-y-auto outline-none prose-sm empty:before:content-[attr(data-placeholder)] empty:before:text-[#9CA3AF] before:pointer-events-none"
        style={{ lineHeight: '1.5' }}
      />
    </div>
  );
}
